import { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Alert, CircularProgress, Link, Stack, Typography } from "@mui/material";
import PageShell from "../components/PageShell";
import { fetchOpenUrl } from "../api";
import { trackEvent } from "../analytics";

// Card #971 — root cause was View/Download baking a presigned S3 URL (300s
// TTL) into the page at LOAD time; a rider who waited past the TTL then
// clicked navigated straight to S3's raw "token expired" XML. This page is
// the fix: SheetsPage now links HERE instead, and the URL is minted at CLICK
// time (this page's mount), never earlier — so an expired-token page is
// structurally impossible. Always a branded interstitial; a raw AWS error
// must never reach the browser.
const POLL_INTERVAL_MS = 15000;
const MAX_POLL_ATTEMPTS = 8; // ~2 minutes, matching SheetsPage's own render wait

export default function OpenPage() {
  const { requestId, rideId, variant } = useParams();
  // loading | redirecting | generating | stalled | notfound | unavailable | error
  const [state, setState] = useState("loading");
  const attemptsRef = useRef(0);
  const timeoutRef = useRef(null);

  const attempt = useCallback(async () => {
    try {
      const { status, body } = await fetchOpenUrl({ requestId, rideId, variant });
      if (status === 200 && body.url) {
        setState("redirecting");
        // #976 — GA4 sheet_opened: fires once per successful open, right
        // before the hand-off to the presigned URL. No PII: the variant name
        // only (never requestId/rideId/rider). transport_type "beacon" so the
        // hit survives the immediate navigation away. No-ops when
        // VITE_GA_MEASUREMENT_ID is unset (see ../analytics).
        trackEvent("sheet_opened", { variant, transport_type: "beacon" });
        window.location.replace(body.url);
        return;
      }
      if (status === 202) {
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
          setState("stalled");
          return;
        }
        setState("generating");
        timeoutRef.current = setTimeout(attempt, POLL_INTERVAL_MS);
        return;
      }
      if (status === 404) {
        setState("notfound");
        return;
      }
      if (status === 410) {
        setState("unavailable");
        return;
      }
      setState("error");
    } catch {
      setState("error");
    }
  }, [requestId, rideId, variant]);

  useEffect(() => {
    attempt();
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [attempt]);

  const backToSheets = (
    <Link component={RouterLink} to={`/sheets/${requestId}`}>
      Back to your scoresheets
    </Link>
  );

  if (state === "loading" || state === "redirecting") {
    return (
      <PageShell>
        <Stack spacing={2} alignItems="center" sx={{ mt: 4 }}>
          <CircularProgress />
          <Typography align="center" sx={{ color: "text.secondary" }}>
            {state === "redirecting"
              ? "Opening your scoresheet…"
              : "Preparing your scoresheet…"}
          </Typography>
        </Stack>
      </PageShell>
    );
  }

  if (state === "generating") {
    return (
      <PageShell>
        <Stack spacing={2} alignItems="center" sx={{ mt: 4 }}>
          <CircularProgress />
          <Typography align="center" sx={{ color: "text.secondary" }}>
            Your scoresheet is being generated — this page will open it
            automatically as soon as it&apos;s ready.
          </Typography>
        </Stack>
      </PageShell>
    );
  }

  if (state === "stalled") {
    return (
      <PageShell>
        <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ width: "100%" }}>
            This is taking longer than expected. Please try again in a
            minute, or {backToSheets}.
          </Alert>
        </Stack>
      </PageShell>
    );
  }

  if (state === "notfound") {
    return (
      <PageShell>
        <Alert severity="warning">This link wasn&apos;t found.</Alert>
      </PageShell>
    );
  }

  if (state === "unavailable") {
    return (
      <PageShell>
        <Stack spacing={2}>
          <Alert severity="error">
            This scoresheet is no longer available. Please visit the show
            office for assistance.
          </Alert>
          {backToSheets}
        </Stack>
      </PageShell>
    );
  }

  // error
  return (
    <PageShell>
      <Stack spacing={2}>
        <Alert severity="error">
          Something went wrong opening your scoresheet. Please try again
          later.
        </Alert>
        {backToSheets}
      </Stack>
    </PageShell>
  );
}
